import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Wrench, Sparkles, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ClaudeTaskCommand } from '@/types/claudeTask';

interface DiffEvent {
  id: string;
  description?: string;
  category?: string;
}

interface FixDiffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  diffId: string;
  slug: string;
  period: string;
  hook?: string;
  events?: DiffEvent[];
  onTaskCreated?: () => void;
}

// Suggested prompts for common fixes
const SUGGESTED_PROMPTS = [
  {
    label: 'Improve tags',
    prompt: 'Review and improve the event tags. Ensure tags accurately reflect the pricing changes (e.g., price_increase, price_decrease, new_tier, feature_added, feature_removed, limit_change, etc.).',
  },
  {
    label: 'Fix categories',
    prompt: 'Review the event categories and fix any that are incorrectly assigned. Ensure pricing_structure, feature, limits, and misc are used appropriately.',
  },
  {
    label: 'Add annotations',
    prompt: 'Review the events and add annotation coordinates for any events that are missing them. Ensure annotations accurately point to the relevant UI changes.',
  },
  {
    label: 'Rewrite hook',
    prompt: 'Rewrite the diff hook/headline to be more compelling and accurately summarize the key pricing changes.',
  },
];

// Truncate text with ellipsis
const truncate = (text: string, maxLength: number) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

// Capitalize category name (e.g., "pricing_structure" -> "Pricing Structure", "limits" -> "Limits")
const formatCategory = (category: string | undefined) => {
  if (!category) return 'Misc';
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export function FixDiffModal({
  open,
  onOpenChange,
  diffId,
  slug,
  period,
  hook,
  events = [],
  onTaskCreated,
}: FixDiffModalProps) {
  const [prompt, setPrompt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      toast({
        title: 'Prompt required',
        description: 'Please enter instructions for the fix',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert({
          command: ClaudeTaskCommand.FIX_DIFF,
          slug,
          params: {
            slug,
            period,
            prompt: prompt.trim(),
            news_id: diffId,
          },
          status: 'pending',
          priority: 1, // High priority
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Fix task created',
        description: `Fix diff task for "${slug}" has been queued with high priority`,
      });

      setPrompt('');
      onOpenChange(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating fix_diff task:', error);
      toast({
        title: 'Error creating task',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuggestedPrompt = (suggestedPrompt: string) => {
    setPrompt(prev => prev ? `${prev}\n\n${suggestedPrompt}` : suggestedPrompt);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-500" />
            Fix Diff
          </DialogTitle>
          <DialogDescription>
            Create a high-priority task to fix issues with this diff.
            <br />
            <span className="text-xs text-muted-foreground mt-1 block">
              {slug} • {period}
            </span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current hook display */}
          {hook && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
              <span className="text-gray-500 text-xs font-medium">Current hook:</span>
              <p className="text-gray-700 mt-1">{hook}</p>
            </div>
          )}

          {/* Suggested prompts */}
          <div className="space-y-2">
            <Label className="text-xs text-gray-500">Quick suggestions:</Label>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_PROMPTS.map((suggestion, idx) => (
                <Button
                  key={idx}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleSuggestedPrompt(suggestion.prompt)}
                >
                  <Sparkles className="h-3 w-3 mr-1 text-amber-500" />
                  {suggestion.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Event-specific double check suggestions */}
          {events && events.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Double check events:</Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {events.map((event) => (
                  <Button
                    key={event.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs text-left justify-start"
                    onClick={() => handleSuggestedPrompt(`Double check the "${formatCategory(event.category)}" event: "${event.description}". Verify it's correctly categorized and the description accurately reflects the change.`)}
                    title={event.description}
                  >
                    <Search className="h-3 w-3 mr-1 text-blue-500 flex-shrink-0" />
                    <span className="truncate">
                      {formatCategory(event.category)}: {truncate(event.description || 'No description', 30)}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Prompt input */}
          <div className="space-y-2">
            <Label htmlFor="prompt">Instructions for Claude</Label>
            <Textarea
              id="prompt"
              placeholder="Describe what needs to be fixed or improved..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Be specific about what you want changed. Claude will analyze the diff and make improvements.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !prompt.trim()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Wrench className="h-4 w-4 mr-2" />
                  Create Fix Task
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
